import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import type { OSCE } from '@ils/osces/db/osce.js';
import { prettyConversationType, prettyOsceCategory } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

/**
 * @element ils-view-osce
 */
@customElement('ils-view-osce')
export class ILSViewOSCE extends ChemistryLitElement {
  @property({ attribute: false }) osce = new RXController<null | OSCE>(this, null);
  @property({ type: Boolean, attribute: 'show-back' }) showBack = false;

  @state() private readonly branding = new TranslationController(this);

  override render() {
    return html`
      <x-flex padding="md" ${css({ overflow: 'auto', width: '100%' })}>
        <x-flex space="xl" ${css({ maxWidth: this.sizes.maxPageWidth })}> ${this._viewOsce()} </x-flex>
      </x-flex>
    `;
  }

  private _viewOsce() {
    if (!this.osce.value)
      return html`
        <x-empty-placeholder
          .text=${`${this.branding.translate('osces') as string} konnten nicht geladen werden`}
          .img=${'close'}
        ></x-empty-placeholder>
      `;
    return html`
      <x-flex padding="md" space="lg" ${css({ width: '100%', maxWidth: this.sizes.maxPageWidth })}>
        <x-flex flex horizontal crossaxis-center space="sm">
          ${this.showBack
            ? html`
                <x-button
                  mode="icon"
                  icon="arrow_back"
                  @click=${() => {
                    try {
                      window.history.back();
                    } catch {
                      goTo({ path: '/start/content-osce', searchParams: NO_CHANGE });
                    }
                  }}
                ></x-button>
              `
            : nothing}
          <x-h2> ${this.osce.value.title} </x-h2>
        </x-flex>
        <x-grid space="md" columns="max-content 1fr">
          <x-text bold> Titel Prüfling </x-text>
          <x-text> ${this.osce.value.titleTrainee} </x-text>
          <x-text bold> OSCE Typ </x-text>
          <x-text> ${prettyOsceCategory(this.osce.value.osceType)} </x-text>
          ${this._gridSpacer()} ${this._additionalInfo()} ${this._gridSpacer()} ${this._descriptions()}
        </x-grid>
        ${this._instructionCategories()}
      </x-flex>
    `;
  }

  private _gridSpacer() {
    return html` <x-flex padding="sm" ${css({ gridColumn: '1 / span 2' })}></x-flex> `;
  }

  private _additionalInfo() {
    if (!this.osce.value) return nothing;
    return html`
      <x-text bold> Stationstyp </x-text>
      <x-text> ${this.osce.value.taskType === 'text' ? 'Text' : 'Konversation'} </x-text>
      ${this.osce.value.taskType === 'voice'
        ? html`
            <x-text bold> Konversationstyp </x-text>
            <x-text> ${prettyConversationType(this.osce.value.conversationType)} </x-text>
            <x-text bold> Maximale Vorbereitungszeit </x-text>
            <x-text> ${this.osce.value.startButtonDuration} Sekunden </x-text>
            <x-text bold> Video aktiviert? </x-text>
            <x-text> ${this.osce.value.enableVideo ? 'Ja' : 'Nein'} </x-text>
          `
        : nothing}
      <x-text bold> Dauer </x-text>
      <x-text> ${this.osce.value.durationInMinutes} Minuten </x-text>
      ${this.osce.value.videoFileID
        ? html`
            <video
              controls
              ${css({ gridColumn: '1 / span 2', width: '100%' })}
              src=${AdornisFile.getServeLink(this.osce.value.videoFileID)}
            ></video>
          `
        : nothing}
    `;
  }

  private _descriptions() {
    if (!this.osce.value) return nothing;
    return html`
      <x-flex ${css({ gridColumn: '1 / span 2' })} space="sm">
        <x-text bold> Kurzbeschreibung </x-text>
        <x-basic-card padding="md" no-elevation ${css({ width: '100%' })}>
          <x-text> ${unsafeHTML(this.osce.value.text?.shortDescription || '-')} </x-text>
        </x-basic-card>
      </x-flex>
      <x-flex ${css({ gridColumn: '1 / span 2' })} space="sm">
        <x-text bold> Fallbeschreibung (Prüfling) </x-text>
        <x-basic-card padding="md" no-elevation ${css({ width: '100%' })}>
          <x-text> ${unsafeHTML(this.osce.value.text?.sceneDescriptionTrainee || '-')} </x-text>
        </x-basic-card>
      </x-flex>
      <x-flex ${css({ gridColumn: '1 / span 2' })} space="sm">
        <x-text bold> Aufgabentext (Prüfling) </x-text>
        <x-basic-card padding="md" no-elevation ${css({ width: '100%' })}>
          <x-text> ${unsafeHTML(this.osce.value.text?.taskDescriptionTrainee || '-')} </x-text>
        </x-basic-card>
      </x-flex>
      <x-flex ${css({ gridColumn: '1 / span 2' })} space="sm">
        <x-text bold> Szenenbeschreibung (Schauspieler) </x-text>
        <x-basic-card padding="md" no-elevation ${css({ width: '100%' })}>
          <x-text> ${unsafeHTML(this.osce.value.text?.sceneDescriptionTester || '-')} </x-text>
        </x-basic-card>
      </x-flex>
    `;
  }

  private _instructionCategories() {
    if (!this.osce.value?.has('instructionCategories') || !this.osce.value.has('isRatedAtLeastOnce')) return nothing;
    if (!this.osce.value.instructionCategories?.length) return nothing;
    return html`
      <x-flex space="lg">
        <x-h3> Bewertungskriterien </x-h3>
        ${repeat(
          this.osce.value.instructionCategories,
          (instructionCategory, i) =>
            html`
              <x-flex space="sm">
                <x-h4> Kategorie ${i + 1} </x-h4> <x-text> ${instructionCategory.description} </x-text>
                <x-flex space="sm" ${css({ marginLeft: this.spacing.lg })}>
                  ${repeat(
                    instructionCategory.instructions,
                    (instruction, i) =>
                      html`
                        <x-basic-card padding="md" no-elevation ${css({ width: '100%' })}>
                          <x-flex space="md">
                            <x-text bold> Frage ${i + 1} </x-text>
                            <x-grid space="sm" columns="max-content 1fr">
                              <x-flex> <x-text> Frage </x-text> </x-flex>
                              <x-flex> <x-text> ${unsafeHTML(instruction.question)} </x-text> </x-flex>
                              <x-flex> <x-text> Beschreibung </x-text> </x-flex>
                              <x-flex> <x-text> ${unsafeHTML(instruction.description)} </x-text> </x-flex>
                            </x-grid>
                          </x-flex>
                        </x-basic-card>
                      `,
                  )}
                </x-flex>
              </x-flex>
            `,
        )}
      </x-flex>
    `;
  }
}
